import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import MagicLinkVerifyPage from './pages/MagicLinkVerifyPage';
import DashboardLayout from './components/DashboardLayout';
import DashboardOverview from './pages/DashboardOverview';
import CallsPage from './pages/CallsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import UsersPage from './pages/UsersPage';
import OrdersPage from './pages/OrdersPage';
import MonitoringPage from './pages/MonitoringPage';
import BusinessProfilePage from './pages/BusinessProfilePage';
import ConversationFlowPage from './pages/ConversationFlowPage';
import KnowledgeRepositoryPage from './pages/KnowledgeRepositoryPage';
import StaffPage from './pages/StaffPage';
import ContactsPage from './pages/ContactsPage';
import SalesCampaignsPage from './pages/SalesCampaignsPage';
import SalesPipelinePage from './pages/SalesPipelinePage';
import EmployeeCommunicationsPage from './pages/EmployeeCommunicationsPage';
import DemoAreaPage from './pages/DemoAreaPage';
import IVRAudioAssetsPage from './pages/IVRAudioAssetsPage';
import TechnicalExpertPage from './pages/TechnicalExpertPage';
import LandingPage from './pages/LandingPageEnhanced';
import AffiliatesPage from './pages/AffiliatesPage';
import LoadingSpinner from './components/LoadingSpinner';
import AffiliateApplicationPage from './pages/AffiliateApplicationPage';
import SupportPage from './pages/SupportPage';
import ContactPage from './pages/ContactPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import QADashboardPage from './pages/QADashboardPage';
import QATestSuitesPage from './pages/QATestSuitesPage';
import QATestRunPage from './pages/QATestRunPage';
import QAConfigPage from './pages/QAConfigPage';
import IntegrationsPage from './pages/IntegrationsPage';
import LiveCallsPage from './pages/LiveCallsPage';
import DialerPage from './pages/DialerPage';
import CallScreeningPage from './pages/CallScreeningPage';
import DemoPage from './pages/DemoPage';
import IndustryLandingPage from './pages/industries/IndustryLandingPage';
import HomePage from './pages/HomePage';
import TransportationPage from './pages/TransportationPage';
import DriverLandingPage from './pages/DriverLandingPage';
// /privacy and /terms are served as standalone static HTML (frontend/public/*)
// via Netlify rewrites — not React routes — so their raw URLs return real,
// crawlable content for SMS/10DLC compliance review.
import { dentalConfig, medspaConfig, vetConfig, hvacConfig, plumbingConfig, roofingConfig, lawConfig, truckingConfig, dispatcherConfig, brokersConfig, totalConfig, pestControlConfig, propertyManagementConfig, callCentersConfig, alfred346Config } from './config/industryConfigs';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public marketing pages */}
      {/* / is the GENERIC capability pitch (inbound + outbound, multi-agent) with an
          "areas we cover" grid. Every tile in that grid must resolve to a real route
          below — a tile pointing at a <Navigate to="/"> is a loop back to itself. */}
      <Route path="/" element={<HomePage />} />
      {/* /transportation is the carrier & fleet vertical — this page WAS the homepage
          while the site was transportation-only (2026-07-27 → 2026-08-30). The content
          moved here unchanged, so /driver-recruiting now redirects HERE, not to /. */}
      <Route path="/transportation" element={<TransportationPage />} />
      <Route path="/driver-recruiting" element={<Navigate to="/transportation" replace />} />
      {/* Medical/dental was dropped 2026-07-27; the URL is kept as a redirect so old
          links and ads still land somewhere real. */}
      <Route path="/patient-reactivation" element={<Navigate to="/" replace />} />
      {/* /drivers is the DRIVER-facing side of the marketplace — free concierge,
          carrier vetting. A different audience from /transportation, which sells
          carriers on calling their applicants; a driver who lands on the carrier
          pitch bounces immediately. */}
      <Route path="/drivers" element={<DriverLandingPage />} />
      {/* Previous homepage (inbound "Voice Executive Assistant" positioning) */}
      <Route path="/total" element={<IndustryLandingPage config={totalConfig} />} />
      <Route path="/classic" element={<LandingPage />} />
      <Route path="/affiliates" element={<AffiliatesPage />} />
      <Route path="/affiliates/apply" element={<AffiliateApplicationPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/demo" element={<DemoPage />} />

      {/* Industry landing pages.
          These seven still redirect to the homepage. They were switched off on
          2026-07-30 when the site went transportation-only, and the 2026-08-30
          generic-homepage change did NOT bring them back — the homepage lists only
          the areas we actively sell. Their configs are still in industryConfigs.js;
          restoring one means a route here, a tile in HomePage's AREAS, and an entry
          in public-routes.mjs — all three, or the page is an orphan. */}
      <Route path="/industries/dental" element={<Navigate to="/" replace />} />
      <Route path="/industries/medspa" element={<Navigate to="/" replace />} />
      <Route path="/industries/vet" element={<Navigate to="/" replace />} />
      <Route path="/industries/hvac" element={<Navigate to="/" replace />} />
      <Route path="/industries/plumbing" element={<Navigate to="/" replace />} />
      <Route path="/industries/roofing" element={<Navigate to="/" replace />} />
      <Route path="/industries/law" element={<Navigate to="/" replace />} />
      <Route path="/industries/trucking" element={<IndustryLandingPage config={truckingConfig} canonicalPath="/industries/trucking" />} />
      <Route path="/industries/dispatchers" element={<IndustryLandingPage config={dispatcherConfig} canonicalPath="/industries/dispatchers" />} />
      <Route path="/industries/brokers" element={<IndustryLandingPage config={brokersConfig} canonicalPath="/industries/brokers" />} />
      {/* 346 self-demo line — "hear Alfred call you" consent → callback landing page */}
      <Route path="/industries/alfred" element={<IndustryLandingPage config={alfred346Config} />} />
      {/* /hvac alias for the standalone HVAC campaign URL */}
      <Route path="/hvac" element={<Navigate to="/" replace />} />
      {/* /alfred alias — shorter campaign URL for the 346 self-demo line */}
      <Route path="/alfred" element={<IndustryLandingPage config={alfred346Config} />} />
      {/* Back online 2026-08-30: the generic homepage lists these three as areas we
          cover, and a tile has to point at a real page. They were taken offline while
          the site was transportation-only. Also re-added to public-routes.mjs. */}
      <Route path="/industries/pest-control" element={<IndustryLandingPage config={pestControlConfig} canonicalPath="/industries/pest-control" />} />
      <Route path="/industries/property-management" element={<IndustryLandingPage config={propertyManagementConfig} canonicalPath="/industries/property-management" />} />
      <Route path="/industries/call-centers" element={<IndustryLandingPage config={callCentersConfig} canonicalPath="/industries/call-centers" />} />

      {/* Auth routes */}
      <Route
        path="/login"
        element={!user ? <LoginPage /> : <Navigate to="/app" replace />}
      />
      <Route
        path="/register"
        element={!user ? <RegisterPage /> : <Navigate to="/app" replace />}
      />
      <Route
        path="/auth/verify"
        element={<MagicLinkVerifyPage />}
      />

      {/* Protected app routes */}
      <Route
        path="/app"
        element={user ? <DashboardLayout /> : <Navigate to="/login" replace />}
      >
        <Route index element={<DashboardOverview />} />
        <Route path="calls" element={<CallsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="monitoring" element={<MonitoringPage />} />
        <Route path="business-profile" element={<BusinessProfilePage />} />
        <Route path="conversation-flows" element={<ConversationFlowPage />} />
        <Route path="knowledge" element={<KnowledgeRepositoryPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="call-screening" element={<CallScreeningPage />} />
        <Route path="sales-campaigns" element={<SalesCampaignsPage />} />
        <Route path="sales-pipeline" element={<SalesPipelinePage />} />
        <Route path="employee-communications" element={<EmployeeCommunicationsPage />} />
        <Route path="technical-expert" element={<TechnicalExpertPage />} />
        <Route path="demo-area" element={<DemoAreaPage />} />
        <Route path="ivr-audio-assets" element={<IVRAudioAssetsPage />} />
        <Route path="admin" element={<AdminDashboardPage />} />
        <Route path="qa" element={<QADashboardPage />} />
        <Route path="qa/config" element={<QAConfigPage />} />
        <Route path="qa/suites" element={<QATestSuitesPage />} />
        <Route path="qa/suites/new" element={<QATestSuitesPage />} />
        <Route path="qa/suites/:suiteId" element={<QATestSuitesPage />} />
        <Route path="qa/runs/:runId" element={<QATestRunPage />} />
        <Route path="qa/executions/:executionId" element={<QATestRunPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="live-calls" element={<LiveCallsPage />} />
        <Route path="dialer" element={<DialerPage />} />
      </Route>

      {/* Backwards compatibility - redirect old dashboard paths */}
      <Route path="/dashboard" element={<Navigate to="/app" replace />} />
      <Route path="/dashboard/*" element={<Navigate to="/app" replace />} />

      {/* 404 fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
